import { api } from 'store/api';
import { ApiResponse } from 'store/api/types';
import { ReturnType, Section, Subsection, TaxRate } from './models/returnData';
import { Mapping } from 'store/organisation/models';
import { MappingData, DefaultTaxRatesData } from './models';
import { ReturnFrequency } from 'store/api/enums';
import { TOKEN_ID_HEADER } from 'store/app/constants';
import { isMaltaTifdReturnType } from 'utils/tifd-mapping';

export interface GetReturnTypesResponse extends ApiResponse {
  data: { returnTypes: ReturnType[]; returnTypesEdit: ReturnType[] };
}
export const getReturnTypes = (
  connectionId: string
): Promise<GetReturnTypesResponse> =>
  api.get<GetReturnTypesResponse>(`/auth/returns/${connectionId}/returnTypes`, {
    headers: {
      [TOKEN_ID_HEADER]: connectionId,
    },
  });

export interface GetTemplatesResponse extends ApiResponse {
  data: { templates: Mapping[] };
}
export const getTemplates = (): Promise<GetTemplatesResponse> =>
  api.get<GetTemplatesResponse>(`/auth/returns/templates`);

export interface GetReturnFrequencyResponse extends ApiResponse {
  data: { frequencies: ReturnFrequency };
}
export const getFrequency = (): Promise<GetReturnFrequencyResponse> =>
  api.get<GetReturnFrequencyResponse>(`/auth/returns/frequency`);

export interface GetSectionsResponse extends ApiResponse {
  data: { sections: Section[]; subsections: Subsection[] };
}
export const getSections = (
  connectionId: string,
  returnTypeId: string
): Promise<GetSectionsResponse> =>
  api.get<GetSectionsResponse>(
    `/auth/returns/${connectionId}/sectionsAndSubsections?returnTypeId=${returnTypeId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );

export interface GetTaxRatesResponse extends ApiResponse {
  taxRates: TaxRate[];
  taxEnabled: boolean;
  connectionId: string;
}
export type GetTaxRatesOptions = {
  /** When false, returns combined purchase/sale rates (QuickBooks unique list). */
  separatePurchasesAndSales?: boolean;
};

export const getTaxRates = (
  connectionId: string,
  options?: GetTaxRatesOptions
): Promise<GetTaxRatesResponse> =>
  api.get<GetTaxRatesResponse>(`/auth/returns/${connectionId}/taxRates`, {
    headers: {
      [TOKEN_ID_HEADER]: connectionId,
    },
    // 401 here is a broken provider connection, not an expired user session.
    skipAuthRefresh: true,
    query:
      options?.separatePurchasesAndSales === false
        ? { separatePurchasesAndSales: 'false' }
        : undefined,
  });

export interface PostMappingResponse extends ApiResponse {
  data: { mapping: Mapping };
}
export const postMapping = (
  connectionId: string,
  mappingData: MappingData
): Promise<PostMappingResponse> => {
  const { tifdAccountMappings, tifdActivities, ...mappingBody } = mappingData;
  const newJSON = isMaltaTifdReturnType(mappingData.typeId)
    ? {
        mapping: tifdAccountMappings ?? [],
        activities: tifdActivities ?? [],
      }
    : { mapping: mappingData.jsonMapping };
  return api.post<PostMappingResponse>(
    `/auth/returns/${connectionId}/saveMapping`,
    {
      body: {
        ...mappingBody,
        jsonMapping: JSON.stringify(newJSON),
      },

      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );
};

export const deleteMapping = (
  organisationId: string,
  mappingId: string
): Promise<ApiResponse> => {
  return api.delete<ApiResponse>(
    `/auth/returns/organisation/${organisationId}/mapping/${mappingId}/delete`,
    {
      headers: {
        [TOKEN_ID_HEADER]: organisationId,
      },
    }
  );
};

export interface GetDefaultTaxRatesResponse extends ApiResponse {
  data: DefaultTaxRatesData;
}
export const getDefaultTaxRates = (
  returnTypeId: string,
  connectionId: string
): Promise<GetDefaultTaxRatesResponse> =>
  api.get<GetDefaultTaxRatesResponse>(
    `/rates/default/${returnTypeId}/${connectionId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );

export const mapTaxRatesToBoxes = (
  connectionId: string,
  returnTypeId?: string
): Promise<ApiResponse> => {
  return api.get<ApiResponse>(
    `/connections/${connectionId}/taxRates/report/${returnTypeId}/mapping`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );
};
